/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/App.tsx
 *
 * Rôle :
 *   Composant racine de l'app TNE 2026. Définit le router (React Router v6),
 *   les routes publiques / visiteur / admin, et le squelette layout (header
 *   permanent avec logos, footer permanent avec partenaires).
 *
 *   À ce stade (squelette initial), seules quelques routes sont câblées vers
 *   des écrans placeholder. L'objectif est que `pnpm dev` lance bien sans
 *   erreur, que la page d'accueil s'affiche avec la palette TNE Vosges, et
 *   que la navigation router fonctionne. Les écrans réels viendront depuis
 *   les exports Google Stitch (cf. CLAUDE.md §15).
 *
 * Réutilisable :
 *   Le pattern « lazy routes par rôle + AppLayout commun » est portable tel
 *   quel sur d'autres apps DRANE. Voir comment je gère l'AppLayout dans
 *   `src/components/layout/`.
 *
 * @author Mika
 * @since 2026-05-04
 */

import { lazy, Suspense, useEffect } from 'react';
// FIX 2026-05-07 (PROD A3) — Navigate ajouté pour route index /admin
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from './components/layout';
import { DocumentBranding } from './components/layout/DocumentBranding';
import { ImpersonationBanner } from './components/admin/ImpersonationBanner';
import { EventIdentityProvider } from './context/EventIdentityProvider';
// FIX 2026-05-05 (S12) : RoleGuard câblé sur toutes les routes /admin/*
// sauf /admin/login et /admin/2fa-enroll (qui sont les portes d'entrée
// avant la session 2FA-validée). Cf. SPRINT-S12.md « Suite logique ».
import { RoleGuard } from './components/admin/RoleGuard';
// FIX 2026-05-17 (demande Mika) : garde de session visiteur pour les
// pages personnelles (sinon écran vide → « rien ne marche »).
import RequireVisitor from './components/auth/RequireVisitor';
// FIX 2026-05-17 (Chantier B — demande Mika) : garde générique par
// feature flag. Sémantique « défaut ON » — une section n'est masquée
// QUE si son flag est explicitement coupé au backoffice (jamais d'app
// vidée par flag absent / API HS). Cf. components/system/FeatureGate.tsx
// + CLAUDE.md §13 + HANDOFF §2.B.
import { FeatureGate } from './components/system/FeatureGate';
// FIX 2026-07-05 (LOT 4) : ToastViewport monté au niveau App (une seule
// instance GLOBALE) et plus dans AppLayout — les routes plein écran HORS
// layout (/exposant/ma-fiche, /exposant/mes-ateliers, /exposant/scanner,
// /controle, wizard d'inscription…) n'affichaient AUCUN toast (succès ou
// erreur silencieusement perdus, ex. « Fiche enregistrée. »). Découvert
// par le parcours E2E LOT 4 n° 2.
import { ToastViewport } from './components/ui';

// Lazy loading des pages — réduit le bundle initial (cf. CLAUDE.md §14.6 perf).
const HomePage = lazy(() => import('./pages/public/HomePage'));
const ProgrammePage = lazy(() => import('./pages/public/ProgrammePage'));
const PartenairesPage = lazy(() => import('./pages/public/PartenairesPage'));
// 2026-06-03 (Mika — Sprint E) — Annuaire public des exposants + fiche détail.
const ExposantsPage = lazy(() => import('./pages/public/ExposantsPage'));
const ExposantDetailPage = lazy(() => import('./pages/public/ExposantDetailPage'));
// NOLAS 2026 — Vague 1 / LOT-J (Postes + matching, M9) — intégration chef
// 2026-06-16. Liste publique des postes à pourvoir des exposants publiés
// (api/jobs-public.php) + matching candidat↔offres (api/me/job-matches.php).
const PostesPage = lazy(() => import('./pages/public/PostesPage'));
const PosteDetailPage = lazy(() => import('./pages/public/PosteDetailPage'));
const NotFoundPage = lazy(() => import('./pages/system/NotFoundPage'));
const InstallHelpIos = lazy(() => import('./pages/system/InstallHelpIos'));

// ───── Sprint S6 vague 1 — A:1 → A:5 (cf. SPRINT-S6-vague1.md) ──────────
const SplashPage = lazy(() => import('./pages/system/SplashPage'));
const OnboardingPage = lazy(() => import('./pages/system/OnboardingPage'));
const SelectionRolePage = lazy(() => import('./pages/system/SelectionRolePage'));
const InscriptionPage = lazy(() => import('./pages/system/InscriptionPage'));
const MonEspacePage = lazy(() => import('./pages/visiteur/MonEspacePage'));

// ───── Sprint S6 vague 2 — B:6 → B:10 (cf. SPRINT-S6-vague2.md) ─────────
const MonBadgePage = lazy(() => import('./pages/visiteur/MonBadgePage'));
const MonProgrammePage = lazy(() => import('./pages/visiteur/MonProgrammePage'));
const ScannerPage = lazy(() => import('./pages/exposant/ScannerPage'));
const DetailAtelierPage = lazy(() => import('./pages/public/DetailAtelierPage'));

// ───── Sprint S6 vague 3 — B:11 + C:12 + E:27 + E:29 (C:15 enrichi) ─────
const CartePage = lazy(() => import('./pages/public/CartePage'));
const MediasPage = lazy(() => import('./pages/public/MediasPage'));
const AProposPage = lazy(() => import('./pages/public/AProposPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));

// ───── Sprint S7 — Form builder dynamique (admin) ───────────────────────
// FIX 2026-05-05 (S7) : ajout de la route admin /admin/form-builder.
// L'AppLayout public est conservé en attendant un AppLayoutAdmin dédié
// (cf. SPRINT-S7.md « Suite logique »). Le routage admin réel sera
// gardé par un RoleGuard côté serveur (auth_check_admin) — la page
// déclenche un 401 redirect côté front si la session n'est pas active.
const FormBuilderPage = lazy(() => import('./pages/admin/FormBuilderPage'));

// ───── Lot CIRFA (2026-08-03) — trois fonctions socle ─────────────────────
// Chargées en `lazy` comme le reste de l'administration : ces écrans ne
// concernent qu'une poignée d'organisateurs, ils n'ont rien à faire dans le
// bundle initial que télécharge un visiteur.
/** Répartitions par champ du form builder (âge, niveau, filière…). */
const FormStatsPage = lazy(() => import('./pages/admin/FormStatsPage'));
/** Listes nominatives des groupes — vue organisation (site sécurisé). */
const GroupRosterAdminPage = lazy(
  () => import('./pages/admin/GroupRosterAdminPage'),
);
/** Liste nominative de SON groupe — vue accompagnateur. */
const MonGroupeRosterPage = lazy(
  () => import('./pages/referent/MonGroupeRosterPage'),
);
/** Plan de transport : rotations, affectations, passagers. */
const TransportAdminPage = lazy(() => import('./pages/admin/TransportAdminPage'));
/** Feuille de route imprimable d'une rotation (émargement à la montée). */
const TransportRoadmapPage = lazy(
  () => import('./pages/admin/TransportRoadmapPage'),
);

// ───── Sprint S9 — Web Push (VAPID) — visiteur + admin (FIX 2026-05-13) ───
const NotificationsPrefsPage = lazy(
  () => import('./pages/visiteur/NotificationsPrefsPage'),
);
const PushComposerPage = lazy(() => import('./pages/admin/PushComposerPage'));
// M5/G3 (2026-06-14 — parité admin 2025) — composeur d'e-mails admin de
// masse (Tous/Candidats/Exposants/Organisateurs) — super_admin + admin.
const EmailComposerPage = lazy(() => import('./pages/admin/EmailComposerPage'));
// M5/G4 (2026-06-14 — parité admin 2025) — bilan quotidien par exposant
// (cron 18h) + aperçu + envoi de test — super_admin + admin.
const ExhibitorReportsPage = lazy(() => import('./pages/admin/ExhibitorReportsPage'));

// ───── Sprint S10 — Médias étendus (Webradio / WebTV / Vidéos) ──────────
// FIX 2026-05-15 (S10) : 5 nouvelles routes (cf. SPRINT-S10.md).
const WebradioPage = lazy(() => import('./pages/public/WebradioPage'));
const WebtvPage = lazy(() => import('./pages/public/WebtvPage'));
const VideosPage = lazy(() => import('./pages/public/VideosPage'));
const MediaDetailPage = lazy(() => import('./pages/public/MediaDetailPage'));
const FichePartenairePage = lazy(
  () => import('./pages/public/FichePartenairePage'),
);

// ───── Sprint S11 — CMS pages éditables (Tiptap + workflow) ──────────────
// FIX 2026-05-15 (S11) : 4 nouvelles routes (cf. SPRINT-S11.md).
//   /p/:slug                       → page CMS publique (Markdown rendu).
//   /admin/pages                   → liste admin des pages éditables.
//   /admin/pages/:id               → éditeur Tiptap.
//   /admin/pages/:id/versions      → historique + restauration.
const PageDynamiquePage = lazy(() => import('./pages/public/PageDynamiquePage'));
const PagesListPage = lazy(() => import('./pages/admin/PagesListPage'));
const PageEditorPage = lazy(() => import('./pages/admin/PageEditorPage'));
const PageVersionsPage = lazy(() => import('./pages/admin/PageVersionsPage'));

// ───── Sprint S13 — Feature flags + Mode maintenance (admin) ─────────────
// FIX 2026-05-05 (S13) : 2 nouvelles routes admin (cf. SPRINT-S13.md).
//   /admin/feature-flags  → CRUD + toggle des feature flags (granularité
//                            par module). Source de vérité côté serveur.
//   /admin/maintenance    → activation/désactivation du mode maintenance
//                            global avec confirmation typée + token bypass.
// Les 2 routes sont wrappées avec <RoleGuard> (S12) :
//   feature-flags : ADMIN_ROLES (le serveur restreint à super_admin la
//                    suppression et certains flags eux-mêmes).
//   maintenance   : SUPER_ADMIN_ONLY (cf. PHP super_admin_required).
const FeatureFlagsPage = lazy(() => import('./pages/admin/FeatureFlagsPage'));
const MaintenancePage = lazy(() => import('./pages/admin/MaintenancePage'));

// ───── Sprint S12 — Backoffice rôles + invitations + 2FA UI ──────────────
// FIX 2026-05-05 (S12) : 4 nouvelles routes (cf. SPRINT-S12.md).
//   /admin/users-admin       → CRUD comptes admin (super_admin only).
//   /admin/invitations       → liste/relance/révocation invitations.
//   /accepter-invitation     → page publique d'acceptation d'invitation.
//   /admin/2fa-enroll        → enrôlement de la double authentification.
//                              ⚠ 2026-08-03 : ce n'est plus le parcours en
//                              3 étapes du S12, c'est l'ASSISTANT EN 5 ÉTAPES
//                              du lot « e-mail ou application » (choix de la
//                              méthode / installation / liaison / vérification
//                              / codes de secours).
const UsersAdminPage = lazy(() => import('./pages/admin/UsersAdminPage'));
const InvitationsListPage = lazy(() => import('./pages/admin/InvitationsListPage'));
const AcceptInvitationPage = lazy(() => import('./pages/system/AcceptInvitationPage'));
const TotpEnrollPage = lazy(() => import('./pages/admin/TotpEnrollPage'));

// ───── Lot « 2FA au choix e-mail ou application » (2026-08-03) ───────────
//   /admin/app-2fa   → étape 2 de la connexion pour la méthode « application »
//                      (code à 6 chiffres OU code de secours). Route plein
//                      écran, HORS RoleGuard : la session n'est pas encore
//                      validée à ce stade, exactement comme /admin/email-2fa.
//   /admin/securite  → page « Sécurité » du compte (méthode active, bascule,
//                      codes de secours). Posée DANS <AdminShell> pour hériter
//                      de la garde de rôle et du menu.
//   /admin/securite-comptes → ADMINISTRATION de la 2FA (section C du contrat) :
//                      méthodes proposées, vue « qui utilise quoi »,
//                      réinitialisation d'un compte dont le téléphone est
//                      perdu. Ajoutée en vague 2 : les trois endpoints
//                      existaient sans aucun écran, donc PERSONNE ne pouvait
//                      débloquer quelqu'un à court de codes de secours.
const AppTwoFactorPage = lazy(() => import('./pages/admin/AppTwoFactorPage'));
const SecuriteComptePage = lazy(() => import('./pages/admin/SecuriteComptePage'));
const TwoFactorAdminPage = lazy(() => import('./pages/admin/TwoFactorAdminPage'));

// ───── Sprint S14 — Module Recommandations IA (visiteur + admin) ─────────
// FIX 2026-05-05 (S14) : 2 nouvelles routes (cf. SPRINT-S14.md).
//   /mon-questionnaire-ia  → consentement explicite + questionnaire pour
//                             alimenter les recommandations IA. Refusé
//                             aux mineurs (RGPD art. 8) — défense en
//                             profondeur côté serveur ET front.
//   /admin/ai-config       → backoffice providers + assignations + events.
//                             Master switch `ai.recommendations` OFF par
//                             défaut au build prod.
const MonQuestionnaireIaPage = lazy(
  () => import('./pages/visiteur/MonQuestionnaireIaPage'),
);
const AiConfigPage = lazy(() => import('./pages/admin/AiConfigPage'));

// ───── Sprint S15 — État de l'intégration OSNum 2.0 (super_admin) ────────
// FIX 2026-05-06 (S15) : nouvelle route admin (cf. SPRINT-S15.md).
//   /admin/osnum-status → polling 30 s sur l'événement TNE 2026 côté OSNum
//                          + bouton « Forcer la synchronisation ».
//                          Wrappée par <RoleGuard> SUPER_ADMIN_ONLY (la
//                          purge de cache fichier + audit log côté serveur
//                          sont sensibles).
const OsnumStatusPage = lazy(() => import('./pages/admin/OsnumStatusPage'));

// ───── Sprint S19 — Mode avant/pendant/après — Rétrospective ──────────────
// FIX 2026-05-06 (S19-C) : route publique /retrospective.
//   Comportement : avant/pendant l'événement → message d'attente +
//   compte à rebours ; après → 6 sections (chiffres clés, galerie,
//   replays, témoignages, ressources, CTA feedback OSNum). La phase est
//   pilotée par useEventPhase() (S19-A). Cf. CLAUDE.md §F12.
const RetrospectivePage = lazy(() => import('./pages/public/RetrospectivePage'));

// ───── Sprint S20-B — Connexion backoffice + 2FA e-mail ─────────────────────
// FIX 2026-05-07 (S20-B) : 2 nouvelles routes plein écran (sans AppLayout).
//   /admin/login    → étape 1 : email + mot de passe (LoginAdminPage).
//   /admin/email-2fa → étape 2 : code OTP 6 chiffres reçu par email.
// Ces routes sont volontairement HORS RoleGuard (puisqu'elles précèdent
// la création de la session 2FA-validée). Le serveur S20-A pose son cookie
// HttpOnly + SameSite=Strict après vérification du code OTP.
const LoginAdminPage = lazy(() => import('./pages/admin/LoginAdminPage'));
const EmailTwoFactorPage = lazy(() =>
  import('./pages/admin/EmailTwoFactorPage').then((m) => ({
    default: m.EmailTwoFactorPage,
  })),
);

// ───── Sprint S22 — Mot de passe oublié (admin) ──────────────────────────
// FIX 2026-05-07 (S22) : 2 routes plein écran sans AppLayout (cohérent S20).
//   /admin/password-reset-request → étape 1 : saisie e-mail.
//   /admin/password-reset         → étape 2 : nouveau mot de passe (lien reçu).
// Hors RoleGuard volontairement (parcours pré-auth, comme /admin/login).
const PasswordResetRequestPage = lazy(
  () => import('./pages/admin/PasswordResetRequestPage'),
);
const PasswordResetConfirmPage = lazy(
  () => import('./pages/admin/PasswordResetConfirmPage'),
);

// FIX 2026-05-07 (S21) — Système déploiement par token (super_admin only).
// Permet de pousser des fichiers en prod sans FTP. Cf. CLAUDE.md §5
// entrée S21 + DEPLOY-TOKEN-SECURITY.md. Lazy car page rare et lourde.
const DeployTokensPage = lazy(() => import('./pages/admin/DeployTokensPage'));
// M5/G2 (2026-06-14 — parité admin 2025) — module webhooks : abonnements
// HTTP signés HMAC + file de livraison + retry + journal (super_admin only).
const WebhooksAdminPage = lazy(() => import('./pages/admin/WebhooksAdminPage'));
// NOLAS 2026 — M5 / G8 (Mika 2026-06-14) — outil admin : fusion de 2 comptes
// visiteurs en doublon (repointage participation + suppression). super_admin.
const MergeVisitorsPage = lazy(() => import('./pages/admin/MergeVisitorsPage'));
// FIX 2026-05-26 — Export consolidé participations (valorisation pro).
const AttendanceExportPage = lazy(
  () => import('./pages/admin/AttendanceExportPage'),
);

// ───── Chantier A 2026-05-17 — Coquille backoffice unifiée (CDC §21) ─────
// AdminShell = layout route parent : RoleGuard ADMIN_ROLES + sidebar
// groupée unique (les ~27 sections) + top-bar sobre + <Outlet/>. Réponse
// au constat « le menu n'expose que 4 entrées, ~23 sections orphelines ».
// Cf. HANDOFF-2026-05-17-22-45.md §2.A + CLAUDE.md §8.6 (design Stitch
// centralisé, pas inventé).
const AdminShell = lazy(() => import('./components/admin/AdminShell'));

// ───── Sprint S26b — Intégration des 4 designs Stitch orphelins ─────────
// FIX 2026-05-07 (S26b) — 4 nouvelles routes pour intégrer les designs
// Stitch livrés sans page React correspondante. Données 100 % MOCK pour
// ce sprint, à remplacer par TanStack Query + endpoints PHP en S27.
//   /admin/dashboard → DashboardLivePage (KPIs + ECharts + capacités).
//   /admin/groups    → GroupsAdminPage (CRUD groupes — CDC §10.5).
//   /admin/programs  → ProgramsAdminPage (CRUD ateliers — CDC §10.6).
//   /presse          → PressPage (espace presse public — CDC §F12).
const DashboardLivePage = lazy(() => import('./pages/admin/DashboardLivePage'));
// G1 (Sprint I — parité admin 2025) : dashboard analytique EMPLOI branché sur
// le VRAI endpoint api/admin/analytics-emploi.php (cf. AUDIT-PARITE-ADMIN-2025).
const EmploiAnalyticsPage = lazy(() => import('./pages/admin/EmploiAnalyticsPage'));
const GroupsAdminPage = lazy(() => import('./pages/admin/GroupsAdminPage'));
const ProgramsAdminPage = lazy(() => import('./pages/admin/ProgramsAdminPage'));

// ───── Sprint S28-C — Inscriptions admin + Audit trail (lecture seule) ───
//   /admin/signups → Inscriptions ateliers (CRUD léger : cancel + promote
//                     + export CSV ; RGPD masquage mineurs §10.16.7).
//   /admin/audit   → Journal d'audit (SUPER_ADMIN_ONLY ; détail + export).
const SignupsAdminPage = lazy(() => import('./pages/admin/SignupsAdminPage'));
const AuditAdminPage = lazy(() => import('./pages/admin/AuditAdminPage'));
// FIX 2026-05-11 (S28-A) — WorkshopsAdminPage : CRUD complet des ateliers
// avec PhaseAvailabilityToggle d'emblée. Distinct de ProgramsAdminPage
// (ce dernier est l'ancien design Stitch « gestion_des_programmes » sans
// phases). Endpoint cible : /api/admin/workshops.php?action=… (S28-A).
const WorkshopsAdminPage = lazy(() => import('./pages/admin/WorkshopsAdminPage'));
// FIX 2026-05-11 (S28-B) — MediasAdminPage + PartnersAdminPage : CRUD admin
// pour les médias (Webradio/WebTV/Vidéos) et les partenaires. Wrappent
// chacun leur RoleGuard interne (admin/super_admin/moderateur pour médias,
// admin/super_admin/editeur pour partenaires).
const MediasAdminPage = lazy(() => import('./pages/admin/MediasAdminPage'));
const PartnersAdminPage = lazy(() => import('./pages/admin/PartnersAdminPage'));
const PressPage = lazy(() => import('./pages/public/PressPage'));

// FIX 2026-05-18 (feature tuiles accueil + zone Organisateur, demande
// Mika) — zone « Organisateur » = clone fonctionnel de Partenaires
// (table/endpoints séparés V29, Option B CLAUDE.md §5 2026-05-18) +
// mini-page backoffice pour choisir les 6 tuiles de /mon-espace.
const OrganisateurPage = lazy(() => import('./pages/public/OrganisateurPage'));
const OrganisateursAdminPage = lazy(
  () => import('./pages/admin/OrganisateursAdminPage'),
);
const HomeTilesAdminPage = lazy(() => import('./pages/admin/HomeTilesAdminPage'));

// FIX 2026-05-11 (refonte nav permanente) — PlusPage : drawer secondaire
// accessible depuis l'item « Plus » de la BottomNav (5ème icône). Regroupe
// Médias, Partenaires, Presse, Rétrospective, À propos, Contact, etc.
const PlusPage = lazy(() => import('./pages/public/PlusPage'));
// FIX 2026-05-11 (S30 front) — SettingsAdminPage : édition live des
// paramètres globaux (event/site/rgpd/ai/maintenance/etc.). Toute modif
// passe par /api/admin/settings.php (S30 backend) avec CSRF + audit log.
const SettingsAdminPage = lazy(() => import('./pages/admin/SettingsAdminPage'));

// 2026-07-07 (Mika — cockpit « Étape du salon ») — PilotagePage : pilotage
// UNIQUE de l'ouverture/fermeture du salon (presets + verrous), orchestre
// les réglages existants via /api/admin/pilotage.php (écritures super_admin).
const PilotagePage = lazy(() => import('./pages/admin/PilotagePage'));

// 2026-07-07 (Mika — contrôle d'accès admin par section, V76) — éditeur des
// profils d'accès : par rôle (défaut éditable) + surcharge par compte.
// super_admin uniquement (RoleGuard ci-dessous + serveur access.php).
const PermissionsAdminPage = lazy(() => import('./pages/admin/PermissionsAdminPage'));

// FIX 2026-05-11 (S31-A) — Récupération NOLAS exposants :
//   /admin/exhibitors   → ExhibitorsAdminPage (CRUD + regenerate-code +
//                          impression carte exposant, RoleGuard ADMIN_ROLES)
//   /exposant/login     → ExhibitorLoginPage (route directe sans AppLayout,
//                          login par access_code EXPO______)
//   /exposant/scanner   → ExhibitorScannerPage (route directe sans
//                          AppLayout, scanner ULTRA-agile BarcodeDetector
//                          + ROI 320 + fallback ZXing, garde session côté
//                          composant qui redirige vers /exposant/login)
const ExhibitorsAdminPage = lazy(() => import('./pages/admin/ExhibitorsAdminPage'));
// NOLAS 2026 — M5 / G7 (Mika 2026-06-14) — Import exposants CSV (mapping
// flexible, codes EXPO auto, anti-doublons). super_admin / admin.
const ExhibitorsImportPage = lazy(() => import('./pages/admin/ExhibitorsImportPage'));
// FIX 2026-05-19 (re-câblage auth §5) — ExhibitorLoginPage (login par
// access_code TNEXSID) ABANDONNÉ : l'exposant se connecte via /connexion
// (compte visiteur, mail+code). /exposant/login → redirect /connexion.
const ExhibitorScannerPage = lazy(() => import('./pages/exposant/ExhibitorScannerPage'));
// FIX 2026-05-19 (spec verrouillée §5/§6) — codes EXPO (admin) + module
// Contrôle/émargement organisateur.
const ExhibitorCodesAdminPage = lazy(
  () => import('./pages/admin/ExhibitorCodesAdminPage'),
);
// FIX 2026-05-20 (Option A demande Mika §5/§24) — flux complet
// invitation VIP (token HMAC + e-mail) côté admin. Compagnon d'
// `api/admin/vip-invitations.php` et `_lib/vip-invite.php`.
const VipInvitationsAdminPage = lazy(
  () => import('./pages/admin/VipInvitationsAdminPage'),
);
// FIX 2026-05-20 (Message 7 Mika) — templates de mail VIP éditables.
// Accessible aux VIP_MANAGER_ALLOWED (incluant le rôle vip_manager).
const VipEmailTemplatesAdminPage = lazy(
  () => import('./pages/admin/VipEmailTemplatesAdminPage'),
);
const ControlTypesAdminPage = lazy(
  () => import('./pages/admin/ControlTypesAdminPage'),
);
// NOLAS 2026 — Vague 1 / LOT-G (Sondages, M8) — intégration chef 2026-06-16.
// CRUD admin de sondages ciblés par profil candidat (audience_json) +
// restitution agrégée + export CSV. Endpoint api/admin/surveys.php.
const SurveysAdminPage = lazy(() => import('./pages/admin/SurveysAdminPage'));
// FIX 2026-05-21 (V40) — page dédiée stats temps réel par type de contrôle.
const ControlStatsAdminPage = lazy(
  () => import('./pages/admin/ControlStatsAdminPage'),
);
// FIX 2026-05-21 (V40 phase 3) — page DÉDIÉE par type de contrôle avec
// stats riches (KPI, timeline, heatmap, démographie, top scanners,
// alertes, liste complète paginée, export CSV).
const ControlStatsTypePage = lazy(
  () => import('./pages/admin/ControlStatsTypePage'),
);
// FIX 2026-05-20 (sprint Plan Interactif Niveau 3) — CRUD admin des
// stands/zones du plan (49 zones seedées V39). Référentiel consommé par
// /api/stands.php et la nouvelle CartePage immersive.
const StandsAdminPage = lazy(() => import('./pages/admin/StandsAdminPage'));

// FIX 2026-05-22 (sprint SCANS-TRACKING-STATS — T1) — page heatmap
// dédiée densité cumulée sur le plan + mode plein écran.
const HeatmapPage = lazy(() => import('./pages/admin/HeatmapPage'));

// FIX 2026-05-23 (sprint « finir 100 % » — S10) — recalibrage GPS des
// stands à partir des scans géolocalisés (capture opt-in V45).
const StandPositionsAdminPage = lazy(() => import('./pages/admin/StandPositionsAdminPage'));

// FIX 2026-05-25 (sprint borne — suite Mika) — scripts kiosque
// téléchargeables (Windows/macOS/Linux) + instructions Android/iOS
// pour lancer la borne TNE 2026 en mode kiosque sur grand écran.
const KioskLaunchersPage = lazy(() => import('./pages/admin/KioskLaunchersPage'));

// FIX 2026-05-22 (sprint SCANS-TRACKING-STATS — ST1) — Mode TV grand
// écran (plein écran, sans sidebar, RoleGuard ADMIN_ROLES interne).
const DashboardWallPage = lazy(() => import('./pages/admin/DashboardWallPage'));

// FIX 2026-05-22 (sprint « finir 100 % » — S9) — Mode kiosque iPad
// d'accueil. Route publique HORS AppLayout, sans auth, sans garde.
// Visiteur scanne son badge → affiche fiche RGPD-safe 30s → reset.
const KiosquePage = lazy(() => import('./pages/public/KiosquePage'));

// FIX 2026-05-22 (sprint SCANS-TRACKING-STATS — T3+T4) — Alertes staff
// temps réel (V42).
const StaffAlertsPage = lazy(() => import('./pages/admin/StaffAlertsPage'));

// FIX 2026-05-22 (sprint SCANS-TRACKING-STATS — ST8) — Bilan PDF jour J
// HTML imprimable (Cmd+P → PDF natif), pas de mPDF vendoré.
const DailyReportPage = lazy(() => import('./pages/admin/DailyReportPage'));

// FIX 2026-05-22 (sprint SCANS-TRACKING-STATS — ST2 ❤️) — Coup de cœur
// Mika : dashboard exposant /exposant/mon-stand (KPIs live, top profils,
// timeline, derniers scans, export CSV).
const MonStandPage = lazy(() => import('./pages/exposant/MonStandPage'));
// NOLAS 2026 — M5 / G6 (Mika 2026-06-14) — Page « Mes rencontres » exposant
// éditable (liste exhaustive des check-ins + édition note/intérêt/type +
// filtre + export). Parité NOLAS 2025 exhibitor-meetings.html. Garde session
// exhibitor côté composant. Endpoint api/exhibitor/meetings.php.
const MesRencontresPage = lazy(() => import('./pages/exposant/MesRencontresPage'));
// 2026-06-03 (Mika — Sprint 1) — Parcours exposant (inscription par code EXPO,
// demande de code, fiche complète) + garde d'accès globale au salon.
const MaFichePage = lazy(() => import('./pages/exposant/MaFichePage'));
// NOLAS 2026 — LOT 3 (Mikael 2026-07-05) — Espace ANIMATEUR self-service :
// CRUD de ses ateliers (créneaux + jauge PAR créneau + inscrits vs jauge).
// Garde côté composant (sonde me/badge.php, session visiteur TNEVSID).
const MesAteliersPage = lazy(() => import('./pages/exposant/MesAteliersPage'));
const ExhibitorSignupPage = lazy(() => import('./pages/exposant/ExhibitorSignupPage'));
const ExhibitorRequestCodePage = lazy(() => import('./pages/exposant/ExhibitorRequestCodePage'));
// 2026-08-26 — plus charge ici : la page est desormais un ONGLET de
// ExhibitorsAdminPage, qui l'importe directement. La route historique
// redirige vers cet onglet.
// 2026-08-26 — ecran « Animateurs », pendant de l'ecran Exposants.
const WorkshopHostsAdminPage = lazy(() => import('./pages/admin/WorkshopHostsAdminPage'));
// 2026-08-26 — « Mon mot de passe » : la moitie VISIBLE du droit
// /admin/self-password, qui n'existait qu'en base jusqu'ici.
const SelfPasswordPage = lazy(() => import('./pages/admin/SelfPasswordPage'));

// 2026-06-23 (Mika — Phase 1 onboarding dual) — édition des modèles d'e-mail
// transactionnels (exhibitor_code / workshop_host_code).
const EmailTemplatesAdminPage = lazy(() => import('./pages/admin/EmailTemplatesAdminPage'));
const AccessModeGuard = lazy(() => import('./components/AccessModeGuard'));
// 2026-06-03 (Mika — Sprint D) — Espace exposant « CV reçus » (candidats
// scannés ayant partagé leur CV). Garde session exhibitor côté composant.
const CvRecusPage = lazy(() => import('./pages/exposant/CvRecusPage'));
const ControlePage = lazy(() => import('./pages/visiteur/ControlePage'));
// FIX 2026-05-19 (demande Mika) — liste de TOUS les comptes inscrits par
// rôle/type + révocation d'accès (distincte de /admin/users-admin =
// comptes backoffice). Endpoint api/admin/users.php.
const UsersListAdminPage = lazy(
  () => import('./pages/admin/UsersListAdminPage'),
);
// M5/G5 (2026-06-14 — parité admin 2025) — génération/impression de badges
// visiteurs EN LOT (récents/aujourd'hui/tous/recherche, grille imprimable).
const VisitorBadgesPage = lazy(() => import('./pages/admin/VisitorBadgesPage'));
// FIX 2026-05-19 (journal log §5/§24) — suivi opérationnel jour J des
// connexions/actions organisateurs+exposants (au-dessus de
// tne_admin_audit). Endpoint api/admin/activity.php.
const ActivityLogPage = lazy(
  () => import('./pages/admin/ActivityLogPage'),
);
// FIX 2026-05-20 (journal admin erreurs d'inscription §5/§24) —
// suivi des `user.register.*.fail` (validation, email déjà utilisé,
// token VIP expiré, code EXPO inconnu, etc.). Distinct
// d'/admin/activity (journal opérationnel orga/exposant) et
// /admin/audit (conformité super_admin).
const RegistrationErrorsPage = lazy(
  () => import('./pages/admin/RegistrationErrorsPage'),
);
const TagsAdminPage = lazy(() => import('./pages/admin/TagsAdminPage'));
// NOLAS 2026 — Vague 1 / LOT-H (Contenu éditorial configurable, M7) —
// intégration chef 2026-06-16. Éditeur back-office du contenu (accueil /
// À propos / footer) seedé par V67 → settings content.* (is_public=1),
// lu côté public par useSiteContent + <ContentBlock>. Endpoint
// api/admin/site-content.php.
const ContentAdminPage = lazy(() => import('./pages/admin/ContentAdminPage'));
// FIX 2026-05-18 — gestion des rôles d'inscription (libellé, description,
// ouvrir/fermer) demandée par Mika. Consolide form-builder + flags.
const RolesAdminPage = lazy(() => import('./pages/admin/RolesAdminPage'));
// FIX 2026-05-18 — programme commun par rôle (demande Mika).
const RoleProgramsAdminPage = lazy(
  () => import('./pages/admin/RoleProgramsAdminPage'),
);
const TranslationsAdminPage = lazy(() => import('./pages/admin/TranslationsAdminPage'));
const NavigationAdminPage = lazy(() => import('./pages/admin/NavigationAdminPage'));

// ───── Sprint S31-C — Diffusion live + parcours utilisateur (admin) ──────
// FIX 2026-05-11 (S31-C) — 2 nouvelles routes admin (cf. CDC §13.4 + §10) :
//   /admin/media-streams → config Webradio/WebTV live (ADMIN_ROLES).
//   /admin/user-journey  → suivi détaillé du parcours visiteur (ADMIN_ROLES,
//                          masquage RGPD côté serveur selon rôle).
const MediaStreamsAdminPage = lazy(
  () => import('./pages/admin/MediaStreamsAdminPage'),
);
const UserJourneyAdminPage = lazy(
  () => import('./pages/admin/UserJourneyAdminPage'),
);

// ───── Sprint contrôle d'accès atelier (2026-05-16) ──────────────────────
//   /admin/workshop-attendance → vue présence temps réel par atelier
//   (inscrits / présents / no-show / waitlist / places restantes), polling
//   15 s. RoleGuard ADMIN_ROLES (le serveur revalide auth_check_admin).
const WorkshopAttendancePage = lazy(
  () => import('./pages/admin/WorkshopAttendancePage'),
);

// ───── Sprint S31-B — Login visiteur + badge print + parcours perso ──────
// FIX 2026-05-11 (S31-B) — 3 nouvelles routes (cf. SPRINT-S31-B.md) :
//   /connexion             → LoginVisiteurPage (sans AppLayout, magic code).
//   /mon-badge/print       → MonBadgePrintPage (sans AppLayout, format A6).
//   /mon-parcours          → MonParcoursPage (sous AppLayout, portail
//                             visiteur équivalent NOLAS visitor-portal).
const LoginVisiteurPage = lazy(
  () => import('./pages/visiteur/LoginVisiteurPage'),
);
const MonBadgePrintPage = lazy(
  () => import('./pages/visiteur/MonBadgePrintPage'),
);
const MonParcoursPage = lazy(
  () => import('./pages/visiteur/MonParcoursPage'),
);

// ───── 2026-05-16 — Mise en conformité 100 % CDC + multi-événement ───────
//   /mes-favoris      → F12 favoris visiteur (CDC §13.6/§13.7)
//   /mon-groupe       → F12 « mon groupe » (CDC §10.13)
//   /ma-carte         → §13.1 Attendee Connect (partage QR contact)
//   /admin/event-config → fondation multi-événement (RoleGuard ADMIN_ROLES,
//                          le serveur restreint save à super_admin/admin)
const MesFavorisPage = lazy(() => import('./pages/visiteur/MesFavorisPage'));
// NOLAS 2026 — Vague 1 / LOT-G (Sondages, M8) — page candidat « Mes
// sondages » : liste des sondages ciblés sur son profil + réponse.
// Endpoint api/me/surveys.php. Sous RequireVisitor (visiteur connecté).
const MesSondagesPage = lazy(() => import('./pages/visiteur/MesSondagesPage'));
const MonGroupePage = lazy(() => import('./pages/visiteur/MonGroupePage'));
// FIX 2026-05-19 (demande Mika) — page « Mon profil » : auto-correction
// du profil après connexion (utile pour les 41 organisateurs V35 dont
// prénom/nom sont dérivés de l'e-mail). Endpoint visitor/me/profile.php.
const MonProfilPage = lazy(() => import('./pages/visiteur/MonProfilPage'));
const MaCartePage = lazy(() => import('./pages/visiteur/MaCartePage'));
// FIX 2026-05-23 (sprint « finir 100 % » — S8) — page « Mes contacts »
// Attendee Connect (liste bidirectionnelle des QR échangés).
const MesContactsPage = lazy(() => import('./pages/visiteur/MesContactsPage'));
const EventConfigPage = lazy(() => import('./pages/admin/EventConfigPage'));

// Chantier A (2026-05-17) — la garde de base ADMIN_ROLES est désormais
// portée par <AdminShell> (cf. adminNav.ts ALL_ADMIN_ROLES). App.tsx ne
// conserve que SUPER_ADMIN_ONLY pour les sections strictement réservées
// (matrice §10.16.6) qui gardent un <RoleGuard> interne dans le shell.
const SUPER_ADMIN_ONLY = ['super_admin'] as const;

interface AppProps {
  /** Callback du bootstrap pour retirer le préloader HTML. */
  onReady?: () => void;
}

/**
 * Composant racine. Doit rester mince — la logique métier vit dans les
 * pages et les hooks. Réutilisable : remplacer le set de Routes ci-dessous.
 */
export default function App({ onReady }: AppProps) {
  // On signale au bootstrap que React a bien monté (préloader → fade out).
  // useEffect avec [] : exécuté une seule fois au mount.
  useEffect(() => {
    onReady?.();
  }, [onReady]);

  return (
    // Identité de l'événement actif exposée à tout l'arbre (LOT 3) : chaque
    // écran fait `useEventIdentity()` puis `values={identity}` pour résoudre les
    // tokens {eventName}/{eventDate}/{eventPlace}/{organizer} du catalogue i18n.
    <EventIdentityProvider>
      <Suspense fallback={<div className="tne-loading">Chargement…</div>}>
        {/* Branding runtime du <head> (title / apple-title / theme-color)
            piloté par l'événement actif — multi-salon, sans rebuild. */}
        <DocumentBranding />
      {/* FIX 2026-08-26 — le bandeau d'emprunt d'identite etait monte dans
          AdminShell UNIQUEMENT. Des que l'application repond l'identite
          empruntee (cf. me/role.php), la coquille admin disparait… et avec
          elle le seul bouton « Revenir a mon compte ». Monte ici, il suit
          partout : c'est la garantie ANTI-ENFERMEMENT, meme esprit que
          l'exemption IMPERSONATION_EXEMPT cote serveur. */}
      <ImpersonationBanner />
      <Routes>
        {/* ───── Routes plein écran SANS AppLayout (immersives) ──────────
                Splash : gradient TNE Vosges chaud, logos institutionnels
                directement dans le composant. Onboarding / Sélection de
                rôle / Inscription : header sobre intégré dans la page,
                pas besoin du wrapper global. */}
        <Route path="/splash" element={<SplashPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/role" element={<SelectionRolePage />} />
        <Route path="/inscription" element={<SelectionRolePage />} />
        <Route path="/inscription/:role" element={<InscriptionPage />} />

        {/* ───── Sprint S6 vague 2 — Scanner plein écran (sans layout) ─── */}
        <Route path="/scanner" element={<ScannerPage />} />

        {/* ───── Sprint S31-A — Récupération NOLAS exposants (plein écran)
                /exposant/login   : login par access_code EXPO + 6 chars
                /exposant/scanner : « douchette » ULTRA-agile avec
                  BarcodeDetector natif + fallback ZXing + ROI 320×320 +
                  torch + tap-to-focus. Garde session côté composant
                  qui redirige vers /exposant/login si pas de session.
                Voir api/exhibitor/{login,scan}.php + CLAUDE.md §5
                entrée 2026-05-11. */}
        {/* FIX 2026-05-19 (re-câblage auth §5) — /exposant/login
                ABANDONNÉ (l'exposant = compte visiteur, login /connexion
                mail+code). Redirige toute URL en cache vers /connexion. */}
        <Route
          path="/exposant/login"
          element={<Navigate to="/connexion" replace />}
        />
        <Route path="/exposant/scanner" element={<ExhibitorScannerPage />} />
        {/* FIX 2026-05-22 (sprint SCANS-TRACKING-STATS — ST2 ❤️) —
            Dashboard exposant : KPIs live, top profils, timeline,
            20 derniers scans, export CSV. Auth visiteur TNEVSID +
            role='exhibitor' (garde côté composant). Pattern miroir
            ExhibitorScannerPage. */}
        <Route path="/exposant/mon-stand" element={<MonStandPage />} />
        {/* NOLAS 2026 — M5 / G6 (Mika 2026-06-14) — Mes rencontres éditable.
            Garde session exhibitor côté composant. */}
        <Route path="/exposant/mes-rencontres" element={<MesRencontresPage />} />
        {/* 2026-06-03 (Mika — Sprint 1) — Fiche exposant complète (éditeur
            séparé du dashboard). Garde session exhibitor côté composant. */}
        <Route path="/exposant/ma-fiche" element={<MaFichePage />} />
        {/* LOT 3 (Mikael 2026-07-05) — espace animateur (ateliers + jauge
            par créneau). Éditeur post-inscription, symétrique de ma-fiche. */}
        <Route path="/exposant/mes-ateliers" element={<MesAteliersPage />} />
        {/* ── ESPACE ANIMATEUR (2026-08-26, demande Mika) ──────────────────
            L'espace existait DEJA et etait complet (CRUD des ateliers,
            creneaux, jauges, inscrits) — mais il vivait sous `/exposant/`,
            d'ou l'impression qu'animateur et exposant etaient melanges, et
            aucun bouton n'y menait.
            Il a desormais SON adresse. On ne duplique PAS la page : deux
            copies divergeraient. Et l'ancienne adresse reste servie, car des
            liens et des QR codes ont deja ete distribues (regle du projet :
            on ne casse jamais une URL diffusee). */}
        <Route path="/animateur" element={<Navigate to="/animateur/mes-ateliers" replace />} />
        <Route path="/animateur/mes-ateliers" element={<MesAteliersPage />} />
        {/* 2026-06-03 (Mika — Sprint D) — CV reçus par l'exposant. Garde
            session exhibitor côté composant. */}
        <Route path="/exposant/cv-recus" element={<CvRecusPage />} />
        {/* 2026-06-03 (Mika — Sprint 1) — Parcours exposant PUBLIC (plein
            écran, HORS AccessModeGuard) : inscription par code EXPO + demande
            de code. Doivent rester joignables même en mode exhibitor_only. */}
        <Route path="/exposant/inscription" element={<ExhibitorSignupPage />} />
        <Route path="/exposant/demande-code" element={<ExhibitorRequestCodePage />} />

        {/* ───── Sprint codes EXPO + Contrôle (2026-05-19, spec §6) ──────
                /controle : module ÉMARGEMENT organisateur (plein écran,
                comme /exposant/scanner). Garde côté composant : sonde
                /api/me/badge.php + exige rôle `staff`. Sécurité réelle =
                serveur (control/scan.php refuse 403 si pas organisateur). */}
        <Route path="/controle" element={<ControlePage />} />

        {/* ───── Sprint S31-B — Connexion visiteur (magic code) ──────────
                Plein écran sans AppLayout, parcours linéaire 2 étapes
                (email → code 6 chiffres). Le footer institutionnel est
                rendu dans la page elle-même. Cf. SPRINT-S31-B.md. */}
        <Route path="/connexion" element={<LoginVisiteurPage />} />

        {/* ───── Sprint S31-B — Impression A6 du badge ───────────────────
                Page épurée @media print. Ouverte dans un nouvel onglet
                depuis MonBadgePage. PAS sous AppLayout (impression). */}
        <Route path="/mon-badge/print" element={<MonBadgePrintPage />} />

        {/* ───── Sprint S12 — Acceptation d'invitation (publique) + 2FA enroll
                Ces 2 routes sont volontairement HORS RoleGuard : la première
                est un parcours public (avant que le compte soit activé), la
                seconde s'exécute juste après login.php pendant la phase
                "pending_totp" où la session n'est pas encore 2FA-validée. */}
        <Route path="/accepter-invitation" element={<AcceptInvitationPage />} />
        <Route path="/admin/2fa-enroll" element={<TotpEnrollPage />} />

        {/* ───── Sprint S20-B — Login backoffice + 2FA e-mail ────────────────
                Routes plein écran sans AppLayout. Le RoleGuard les utilise
                comme cible de redirection (`/admin/login?from=…`) quand la
                session admin est invalide ou expirée. La page
                /admin/email-2fa est l'étape 2 OTP par e-mail (S20-A). */}
        <Route path="/admin/login" element={<LoginAdminPage />} />
        <Route path="/admin/email-2fa" element={<EmailTwoFactorPage />} />

        {/* ───── Lot 2FA au choix (2026-08-03) — étape 2 « application » ──
                Même statut que /admin/email-2fa : plein écran, hors
                RoleGuard, atteinte juste après login.php quand la personne
                a choisi de recevoir son code dans une application. */}
        <Route path="/admin/app-2fa" element={<AppTwoFactorPage />} />

        {/* FIX 2026-05-22 (sprint SCANS-TRACKING-STATS — ST1) — Mode TV
            grand écran pour projection PC central DRANE le jour J.
            Route plein écran HORS AdminShell (pas de sidebar/header),
            RoleGuard ADMIN_ROLES interne au composant. Pattern miroir
            de SplashPage (composant autonome). */}
        <Route path="/admin/dashboard/wall" element={<DashboardWallPage />} />

        {/* FIX 2026-05-22 (sprint « finir 100 % » — S9) — Mode kiosque
            iPad d'accueil. Route PUBLIQUE HORS AppLayout, sans auth.
            Visiteur scanne son badge → affiche fiche RGPD-safe (Prénom I.,
            rôle, structure) puis reset au bout de 30s. iPad fixe à la
            borne d'accueil le 27 mai. */}
        <Route path="/kiosque" element={<KiosquePage />} />

        {/* FIX 2026-05-07 (PROD A3) — Route index /admin → /admin/dashboard.
            Defense in depth pour toute redirect manuelle vers /admin
            (post-2FA, post-password-reset, bookmark utilisateur, lien
            d'email d'invitation, etc.). Sans cette route, /admin → 404. */}
        <Route
          path="/admin"
          element={<Navigate to="/admin/dashboard" replace />}
        />

        {/* ───── Sprint S22 — Mot de passe oublié (FIX 2026-05-07) ─────────
                Routes plein écran sans AppLayout, hors RoleGuard (parcours
                pré-auth). Étape 1 saisit l'email, étape 2 saisit le nouveau
                mot de passe via un lien token reçu par mail. */}
        <Route
          path="/admin/password-reset-request"
          element={<PasswordResetRequestPage />}
        />
        <Route
          path="/admin/password-reset"
          element={<PasswordResetConfirmPage />}
        />

        {/* ───── Routes wrappées par le layout permanent (Header + Footer
                  avec logos officiels §8.4 + bandeau partenaires §13.5.4) ── */}
        {/* ═════════════════════════════════════════════════════════════
              CHANTIER A (2026-05-17) — COQUILLE BACKOFFICE UNIFIÉE.
              Toutes les sections /admin/* sont désormais câblées DANS
              <AdminShell> (layout parent : RoleGuard ADMIN_ROLES de base
              + sidebar groupée unique + top-bar + Outlet). Fini les ~23
              URL orphelines sans menu (cf. HANDOFF §2.A, CLAUDE.md §21).
              Les routes strictement super_admin gardent EN PLUS leur
              <RoleGuard> interne (defense in depth, matrice §10.16.6) ;
              les autres sont couvertes par la garde de base du shell
              (ADMIN_ROLES) — moderateur/editeur/auteur/staff y sont déjà.
            ═════════════════════════════════════════════════════════════ */}
        <Route element={<AdminShell />}>
          <Route path="/admin/dashboard" element={<DashboardLivePage />} />
          {/* Lot 2FA au choix (2026-08-03) — page « Sécurité » de MON compte.
              Accessible à tout rôle back-office : chacun gère sa propre
              double authentification, ce n'est pas une section d'admin.
              Branchée au menu depuis la vague 2 (groupe « Mon compte » de
              adminNav.ts) : sans entrée de menu, la porte de sortie du
              dispositif — « revenir à l'e-mail », §B.4 du contrat — n'était
              atteignable qu'en tapant l'URL à la main. */}
          <Route path="/admin/securite" element={<SecuriteComptePage />} />
          {/* Lot 2FA au choix — vague 2 : ADMINISTRATION de la 2FA (§C).
              Rôles admin + super_admin, exactement ce que les endpoints
              acceptent ; l'écriture des méthodes autorisées est en plus
              réservée au super_admin côté serveur. */}
          <Route path="/admin/securite-comptes" element={<TwoFactorAdminPage />} />
          {/* G1 — dashboard analytique emploi (cœur parité 2025, Sprint I). */}
          <Route
            path="/admin/emploi-analytics"
            element={<EmploiAnalyticsPage />}
          />
          <Route path="/admin/workshops" element={<WorkshopsAdminPage />} />
          <Route
            path="/admin/workshop-attendance"
            element={<WorkshopAttendancePage />}
          />
          <Route path="/admin/signups" element={<SignupsAdminPage />} />
          <Route path="/admin/programs" element={<ProgramsAdminPage />} />
          <Route path="/admin/pages" element={<PagesListPage />} />
          <Route path="/admin/pages/:id" element={<PageEditorPage />} />
          <Route
            path="/admin/pages/:id/versions"
            element={<PageVersionsPage />}
          />
          <Route path="/admin/medias" element={<MediasAdminPage />} />
          <Route
            path="/admin/media-streams"
            element={<MediaStreamsAdminPage />}
          />
          <Route path="/admin/partners" element={<PartnersAdminPage />} />
          {/* FIX 2026-05-18 — zone « Organisateur » (clone Partenaires,
              V29) + réglage des tuiles d'accueil. RoleGuard interne aux
              pages (admin/super_admin/editeur ; home-tiles=admin+). */}
          <Route
            path="/admin/organisateurs"
            element={<OrganisateursAdminPage />}
          />
          <Route path="/admin/home-tiles" element={<HomeTilesAdminPage />} />
          <Route path="/admin/tags" element={<TagsAdminPage />} />
          {/* NOLAS 2026 — Vague 1 / LOT-H (Contenu éditorial, M7). RoleGuard
              de base ADMIN_ROLES hérité d'AdminShell ; le serveur revérifie
              super_admin/admin + CSRF dans api/admin/site-content.php. */}
          <Route path="/admin/content" element={<ContentAdminPage />} />
          <Route
            path="/admin/translations"
            element={<TranslationsAdminPage />}
          />
          <Route path="/admin/navigation" element={<NavigationAdminPage />} />
          <Route path="/admin/exhibitors" element={<ExhibitorsAdminPage />} />
          {/* M5/G7 (2026-06-14 — parité admin 2025) — import exposants CSV
              (mapping flexible + codes EXPO auto + anti-doublons).
              Création de comptes + codes → super_admin + admin. */}
          <Route
            path="/admin/exhibitors-import"
            element={
              <RoleGuard requiredRoles={['super_admin', 'admin']}>
                <ExhibitorsImportPage />
              </RoleGuard>
            }
          />
          {/* FIX 2026-05-19 (demande Mika) — tous les comptes inscrits
              par rôle + révocation d'accès. */}
          <Route path="/admin/users" element={<UsersListAdminPage />} />
          {/* M5/G5 (2026-06-14 — parité admin 2025) — badges visiteurs en
              lot (impression au desk d'accueil). ALL_ADMIN_ROLES (AdminShell). */}
          <Route path="/admin/visitor-badges" element={<VisitorBadgesPage />} />
          {/* FIX 2026-05-26 (Mika valorisation pro) — export CSV
              consolidé QUI/QUAND/OÙ pour les attestations carrière. */}
          <Route
            path="/admin/attendance-export"
            element={<AttendanceExportPage />}
          />
          {/* FIX 2026-05-19 (spec §5) — codes EXPO (exposant/organisateur). */}
          <Route
            path="/admin/exhibitor-codes"
            element={<ExhibitorCodesAdminPage />}
          />
          {/* 2026-06-03 (Mika — Sprint 1) — Modération des demandes de code
              exposant (approuver = génère + envoie un code EXPO). */}
          <Route
            path="/admin/workshop-hosts"
            element={<WorkshopHostsAdminPage />}
          />
          {/* 2026-08-26 (signale par Mika) — changer SON propre mot de passe
              depuis son espace. La garde de section V76 s'applique cote
              serveur via ADMIN_SECTION_MAP : retirer le droit ferme l'ecran
              ET l'endpoint, pas seulement l'entree de menu. */}
          <Route
            path="/admin/self-password"
            element={<SelfPasswordPage />}
          />
          {/* 2026-08-26 — FUSION : les demandes sont devenues un ONGLET de
              l'ecran Exposants. On garde la route et on redirige, pour que
              les liens deja distribues, les favoris et l'entree V76
              /admin/exhibitor-code-requests continuent de fonctionner. */}
          <Route
            path="/admin/exhibitor-code-requests"
            element={<Navigate to="/admin/exhibitors?onglet=demandes" replace />}
          />
          {/* 2026-06-23 (Mika — Phase 1 onboarding dual) — modèles d'e-mail
              transactionnels (exhibitor_code / workshop_host_code). */}
          <Route
            path="/admin/email-templates"
            element={<EmailTemplatesAdminPage />}
          />
          {/* FIX 2026-05-20 — flux complet invitation VIP (admin). */}
          <Route
            path="/admin/vip-invitations"
            element={<VipInvitationsAdminPage />}
          />
          {/* FIX 2026-05-20 (Message 7) — templates de mail VIP éditables. */}
          <Route
            path="/admin/vip-email-templates"
            element={<VipEmailTemplatesAdminPage />}
          />
          <Route path="/admin/groups" element={<GroupsAdminPage />} />
          <Route path="/admin/user-journey" element={<UserJourneyAdminPage />} />
          {/* FIX 2026-05-19 (journal log §5/§24) — connexions/actions
              organisateurs+exposants (ALL_ADMIN_ROLES via AdminShell). */}
          <Route path="/admin/activity" element={<ActivityLogPage />} />
          {/* FIX 2026-05-20 (journal admin erreurs inscription §5/§24) */}
          <Route
            path="/admin/registration-errors"
            element={<RegistrationErrorsPage />}
          />
          <Route path="/admin/form-builder" element={<FormBuilderPage />} />
          {/* Lot CIRFA — statistiques par champ personnalisé. Placée juste
              après le générateur de formulaire : on définit les champs ici,
              on en lit les répartitions là. */}
          <Route path="/admin/form-stats" element={<FormStatsPage />} />
          {/* Lot CIRFA — listes nominatives (site sécurisé, V83). */}
          <Route path="/admin/listes-nominatives" element={<GroupRosterAdminPage />} />
          {/* Lot CIRFA — transport de groupes (V84). La feuille de route est
              une route à part : c'est un DOCUMENT, qu'on ouvre, qu'on imprime
              et qu'on partage par son URL. */}
          <Route path="/admin/transport" element={<TransportAdminPage />} />
          <Route
            path="/admin/transport/:rotationId"
            element={<TransportRoadmapPage />}
          />
          <Route path="/admin/roles" element={<RolesAdminPage />} />
          <Route
            path="/admin/role-programs"
            element={<RoleProgramsAdminPage />}
          />
          <Route path="/admin/push" element={<PushComposerPage />} />
          {/* M5/G3 (2026-06-14 — parité admin 2025) — composeur d'e-mails de
              masse. Diffusion sensible → super_admin + admin uniquement. */}
          <Route
            path="/admin/email-composer"
            element={
              <RoleGuard requiredRoles={['super_admin', 'admin']}>
                <EmailComposerPage />
              </RoleGuard>
            }
          />
          {/* M5/G4 (2026-06-14 — parité admin 2025) — bilan quotidien par
              exposant (cron 18h) + aperçu + test. super_admin + admin. */}
          <Route
            path="/admin/exhibitor-reports"
            element={
              <RoleGuard requiredRoles={['super_admin', 'admin']}>
                <ExhibitorReportsPage />
              </RoleGuard>
            }
          />
          <Route path="/admin/pilotage" element={<PilotagePage />} />
          <Route
            path="/admin/access"
            element={
              <RoleGuard requiredRoles={['super_admin']}>
                <PermissionsAdminPage />
              </RoleGuard>
            }
          />
          <Route path="/admin/settings" element={<SettingsAdminPage />} />
          <Route path="/admin/feature-flags" element={<FeatureFlagsPage />} />
          <Route path="/admin/ai-config" element={<AiConfigPage />} />
          <Route path="/admin/event-config" element={<EventConfigPage />} />
          {/* FIX 2026-05-19 (spec §6) — types de contrôle / émargement. */}
          <Route
            path="/admin/control-stats"
            element={<ControlStatsAdminPage />}
          />
          <Route
            path="/admin/control-stats/:typeId"
            element={<ControlStatsTypePage />}
          />
          <Route
            path="/admin/control-types"
            element={<ControlTypesAdminPage />}
          />
          {/* NOLAS 2026 — Vague 1 / LOT-G (Sondages, M8). RoleGuard de base
              ADMIN_ROLES hérité d'AdminShell ; le serveur revérifie
              super_admin/admin/editeur + CSRF dans api/admin/surveys.php. */}
          <Route path="/admin/surveys" element={<SurveysAdminPage />} />
          {/* FIX 2026-05-20 (sprint Plan Interactif Niveau 3) —
              CRUD admin des stands/zones du plan (49 zones V39). */}
          <Route
            path="/admin/stands"
            element={<StandsAdminPage />}
          />
          {/* FIX 2026-05-22 (sprint SCANS-TRACKING-STATS — T1) — Heatmap
              densité cumulée sur le plan SVG + mode plein écran natif. */}
          <Route path="/admin/heatmap" element={<HeatmapPage />} />
          <Route path="/admin/stand-positions" element={<StandPositionsAdminPage />} />
          {/* FIX 2026-05-25 (sprint borne) — page admin de
              téléchargement des scripts kiosque (Windows/macOS/Linux)
              + instructions Android/iOS. RoleGuard ADMIN_ROLES hérité
              de AdminShell. */}
          <Route path="/admin/kiosk-launchers" element={<KioskLaunchersPage />} />
          {/* FIX 2026-05-22 (sprint SCANS-TRACKING-STATS — T3+T4) —
              Alertes staff temps réel (mineurs perdus, saturation salon
              / atelier). V42 + cron 5 min + endpoint admin. */}
          <Route path="/admin/alerts" element={<StaffAlertsPage />} />
          {/* FIX 2026-05-22 (sprint SCANS-TRACKING-STATS — ST8) — Bilan
              jour J HTML imprimable. ?date=YYYY-MM-DD (défaut aujourd'hui).
              Le cron daily-report.php envoie un mail aux super_admins
              avec le lien vers cette page à 18h le 27 mai. */}
          <Route path="/admin/daily-report" element={<DailyReportPage />} />
          {/* Sections strictement super_admin (matrice §10.16.6) —
              RoleGuard interne EN PLUS de la garde de base du shell. */}
          <Route
            path="/admin/users-admin"
            element={
              <RoleGuard requiredRoles={[...SUPER_ADMIN_ONLY]}>
                <UsersAdminPage />
              </RoleGuard>
            }
          />
          <Route
            path="/admin/invitations"
            element={
              <RoleGuard requiredRoles={[...SUPER_ADMIN_ONLY]}>
                <InvitationsListPage />
              </RoleGuard>
            }
          />
          <Route
            path="/admin/maintenance"
            element={
              <RoleGuard requiredRoles={[...SUPER_ADMIN_ONLY]}>
                <MaintenancePage />
              </RoleGuard>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <RoleGuard requiredRoles={[...SUPER_ADMIN_ONLY]}>
                <AuditAdminPage />
              </RoleGuard>
            }
          />
          <Route
            path="/admin/deploy-tokens"
            element={
              <RoleGuard requiredRoles={[...SUPER_ADMIN_ONLY]}>
                <DeployTokensPage />
              </RoleGuard>
            }
          />
          {/* M5/G8 (2026-06-14 — parité admin 2025) — fusion de 2 comptes
              visiteurs en doublon. Action destructive → super_admin only. */}
          <Route
            path="/admin/merge-visitors"
            element={
              <RoleGuard requiredRoles={[...SUPER_ADMIN_ONLY]}>
                <MergeVisitorsPage />
              </RoleGuard>
            }
          />
          <Route
            path="/admin/osnum-status"
            element={
              <RoleGuard requiredRoles={[...SUPER_ADMIN_ONLY]}>
                <OsnumStatusPage />
              </RoleGuard>
            }
          />
          {/* M5/G2 (2026-06-14 — parité admin 2025) — webhooks : secrets HMAC
              + déclenchement d'appels HTTP sortants = SUPER_ADMIN_ONLY. */}
          <Route
            path="/admin/webhooks"
            element={
              <RoleGuard requiredRoles={[...SUPER_ADMIN_ONLY]}>
                <WebhooksAdminPage />
              </RoleGuard>
            }
          />
        </Route>

        {/* ───── Routes wrappées par le layout permanent (Header + Footer
                  avec logos officiels §8.4 + bandeau partenaires §13.5.4) ── */}
        {/* 2026-06-03 (Mika — Sprint 1) — Garde d'accès au salon (access.mode) :
            open → tout passe ; exhibitor_only/maintenance → PreLaunchPage pour
            les pages publiques/visiteur. (Les blocs /admin et /exposant sont
            hors de ce wrapper, donc toujours joignables.) */}
        <Route element={<AccessModeGuard />}>
        <Route element={<AppLayout />}>
          <Route path="/" element={<HomePage />} />
          {/* FIX 2026-05-17 (demande Mika) — pages PERSONNELLES
              visiteur groupées sous RequireVisitor : non connecté →
              /connexion (au lieu d'un écran vide qui faisait croire
              que « rien ne marche »). Les pages publiques (programme,
              ateliers, carte, médias, partenaires…) restent HORS
              garde, accessibles à tous. Cf. CLAUDE.md §24 2026-05-17. */}
          <Route element={<RequireVisitor />}>
            <Route path="/mon-espace" element={<MonEspacePage />} />
            <Route path="/mon-badge" element={<MonBadgePage />} />
            {/* Lot CIRFA (2026-08-03) — espace ACCOMPAGNATEUR : liste
                nominative de son groupe pour un événement en site sécurisé.
                Sous `RequireVisitor` car un accompagnateur est un compte
                ordinaire, pas un administrateur. Le cloisonnement réel (ne
                voir QUE ses groupes) est fait par le SERVEUR, dans la clause
                SQL de api/me/group-roster.php — jamais par cette garde de
                route, qui n'authentifie que « une session existe ». */}
            <Route path="/mon-groupe/liste" element={<MonGroupeRosterPage />} />
            <Route path="/mon-programme" element={<MonProgrammePage />} />
            {/* ───── Sprint S31-B — Portail visiteur "Mon parcours" ──────
                    Équivalent moderne du NOLAS visitor-portal. KPI + timeline
                    des check-ins. Refresh auto pendant phase=during. */}
            <Route path="/mon-parcours" element={<MonParcoursPage />} />
            {/* 2026-05-16 — 100 % CDC : favoris (F12), mon groupe (F12),
                ma carte de contact (§13.1 Attendee Connect). */}
            <Route path="/mes-favoris" element={<MesFavorisPage />} />
            {/* NOLAS 2026 — Vague 1 / LOT-G (Sondages, M8) — sondages
                ciblés sur le profil candidat. Endpoint api/me/surveys.php. */}
            <Route path="/sondages" element={<MesSondagesPage />} />
            <Route path="/mon-groupe" element={<MonGroupePage />} />
            <Route path="/ma-carte" element={<MaCartePage />} />
            <Route path="/mes-contacts" element={<MesContactsPage />} />
            {/* FIX 2026-05-19 (demande Mika) — auto-correction du profil. */}
            <Route path="/mon-profil" element={<MonProfilPage />} />
          </Route>
          {/* ───── Chantier B (2026-05-17) — Sections coupables au
                  backoffice via <FeatureGate>. On RÉUTILISE les flags
                  V3 existants (programme.view / partners.section /
                  carte.view / medias.section) + des flags page.* V26
                  pour les pages institutionnelles. Sémantique défaut
                  ON : couper = acte explicite du backoffice. */}
          <Route element={<FeatureGate flag="programme.view" />}>
            <Route path="/programme" element={<ProgrammePage />} />
            <Route path="/atelier/:slug" element={<DetailAtelierPage />} />
          </Route>
          <Route element={<FeatureGate flag="partners.section" />}>
            <Route path="/partenaires" element={<PartenairesPage />} />
            {/* 2026-06-03 (Mika — Sprint E) — Annuaire public des exposants. */}
            <Route path="/exposants" element={<ExposantsPage />} />
            <Route path="/exposants/:id" element={<ExposantDetailPage />} />
            {/* NOLAS 2026 — Vague 1 / LOT-J (Postes + matching, M9) —
                postes à pourvoir publics. Même famille « annuaire des
                exposants » → gatés par le même flag partners.section
                (recommandation RAPPORT-LOT-J §3.1). Endpoints publics
                api/jobs-public.php (liste + détail), aucune PII référent. */}
            <Route path="/postes" element={<PostesPage />} />
            <Route path="/postes/:id" element={<PosteDetailPage />} />
            <Route path="/partenaire/:slug" element={<FichePartenairePage />} />
          </Route>
          {/* FIX 2026-05-18 — zone publique « Organisateur » (V29).
              FeatureGate « défaut ON » (flag organisateur.section
              absent → rendu, cohérent avec partners.section). */}
          <Route element={<FeatureGate flag="organisateur.section" />}>
            <Route path="/organisateur" element={<OrganisateurPage />} />
          </Route>
          <Route element={<FeatureGate flag="carte.view" />}>
            <Route path="/carte" element={<CartePage />} />
          </Route>
          <Route element={<FeatureGate flag="medias.section" />}>
            <Route path="/medias" element={<MediasPage />} />
            <Route path="/webradio" element={<WebradioPage />} />
            <Route path="/webtv" element={<WebtvPage />} />
            <Route path="/videos" element={<VideosPage />} />
            <Route path="/media/:id" element={<MediaDetailPage />} />
          </Route>
          <Route element={<FeatureGate flag="page.a_propos" />}>
            <Route path="/a-propos" element={<AProposPage />} />
          </Route>
          <Route element={<FeatureGate flag="page.contact" />}>
            <Route path="/contact" element={<ContactPage />} />
          </Route>
          <Route element={<FeatureGate flag="page.presse" />}>
            <Route path="/presse" element={<PressPage />} />
          </Route>
          {/* FIX 2026-05-18 — pages institutionnelles TEXTE 100 %
              éditables au backoffice (/admin/pages, slugs tne_pages),
              rendues via le CMS. Corrige les liens footer
              /mentions-legales /confidentialite /accessibilite qui
              renvoyaient 404 (aucune route). Contenu réel publié par
              la migration V27. */}
          <Route
            path="/mentions-legales"
            element={<PageDynamiquePage slug="mentions-legales" />}
          />
          <Route
            path="/confidentialite"
            element={<PageDynamiquePage slug="confidentialite" />}
          />
          <Route
            path="/accessibilite"
            element={<PageDynamiquePage slug="accessibilite" />}
          />
          <Route element={<FeatureGate flag="page.retrospective" />}>
            {/* ───── Sprint S19 — Rétrospective (publique, multi-phase) ──
                    useEventPhase() pilote l'affichage : before/during →
                    écran d'attente + compte à rebours, after → page
                    complète post-événement. */}
            <Route path="/retrospective" element={<RetrospectivePage />} />
          </Route>

          {/* FIX 2026-05-07 (retour Mika) — alias /media → /medias pour
              tolérer les liens "à la main" sans s. */}
          <Route path="/media" element={<Navigate to="/medias" replace />} />
          <Route path="/install-help-ios" element={<InstallHelpIos />} />

          {/* ───── Sprint S9 — Web Push (préférences visiteur) ─────────── */}
          <Route path="/notifications-prefs" element={<NotificationsPrefsPage />} />

          {/* ───── Sprint S11 — CMS page publique (Markdown rendu) ──────── */}
          <Route path="/p/:slug" element={<PageDynamiquePage />} />

          {/* FIX 2026-05-11 (refonte nav permanente) — /plus = drawer
              secondaire de la BottomNav (5ème item). Pas de RoleGuard,
              accessible à tous. */}
          <Route path="/plus" element={<PlusPage />} />

          {/* ───── Sprint S14 — Questionnaire IA (route VISITEUR ;
                  la page détecte is_minor + master off elle-même). */}
          <Route
            path="/mon-questionnaire-ia"
            element={<MonQuestionnaireIaPage />}
          />

          {/* ───── 404 sous le layout pour cohérence visuelle ──────────── */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        </Route>{/* /AccessModeGuard — Sprint 1 (Mika) */}

        {/* ───── Redirections legacy (NOLAS / JANE) à prévoir plus tard ── */}
        {/* <Route path="/registration.html" element={<Navigate to="/inscription" replace />} /> */}
      </Routes>

      {/* Toasts — UNE seule instance globale (position:fixed), visible sur
          TOUTES les routes, y compris les pages plein écran hors AppLayout
          (fix LOT 4, cf. import ci-dessus). */}
        <ToastViewport />
      </Suspense>
    </EventIdentityProvider>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Idées d'évolution (Mika) :
//  - Brancher un AppLayout commun (header + footer permanent) qui wrappe
//    toutes les routes sauf le splash.
//  - Ajouter les routes /inscription, /mon-badge, /scanner, /carte,
//    /admin/* au fur et à mesure.
//  - Mettre en place un RoleGuard (hook `useRole`) qui filtre les routes
//    admin selon la matrice §10.16.6.
// ──────────────────────────────────────────────────────────────────────────
